/**
 * Utility functions for handling pricing based on user groups
 */

export interface User {
  id: number;
  username: string;
  name: string;
  surname: string;
  company: string;
  erp_id: number;
  country: string;
  city: string;
  email: string;
  password: string;
  phone: string;
  address: string;
  is_active: boolean;
  is_admin: boolean;
  is_client: boolean;
  activate_grid_view: boolean;
  zone: string;
  group?: string;
}

/**
 * ERP-style price matrix
 */
export interface ArticlePrice {
  CMSH: number;
  CMSH1: number;
  CMSH2: number;
  CMSH3: number;
  CMSH4: number;
  CMSH5: number;
  CMSH6: number;
  CMSH7: number;
  CMSH8: number;
  CMSH9: number;
  CMSH10: number;
  CMSH11: number | null;
  CMSH12: number;
  CMSH13: number;
  CMSH14: number;
  CMSH15: number;
  CMSH16: number;
  CMSH17: number;
  CMSH18: number;
  CMSH19: number;
}

/**
 * Normalize Auto Partner TecDoc pricing into ERP ArticlePrice
 */
export const normalizeAutoPartnerPrice = (
  priceNet: number,
  priceRetail: number
): ArticlePrice => {
  return {
    CMSH: priceNet,        // Base price (Group A)
    CMSH1: priceNet,
    CMSH2: priceNet,
    CMSH3: priceNet,
    CMSH4: priceRetail,   // Retail price
    CMSH5: priceNet,
    CMSH6: priceNet,
    CMSH7: priceNet,
    CMSH8: priceNet,
    CMSH9: priceNet,
    CMSH10: priceNet,
    CMSH11: null,
    CMSH12: priceNet,
    CMSH13: priceNet,
    CMSH14: priceNet,
    CMSH15: priceNet,
    CMSH16: priceNet,
    CMSH17: priceNet,
    CMSH18: priceNet,
    CMSH19: priceNet,
  };
};

/**
 * Get the current authenticated user from localStorage
 */
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

/**
 * Get the user's group
 */
export const getUserGroup = (user: User | null): string => {
  return user?.group || "A";
};

/**
 * Map group → price field
 */
export const getPriceFieldByGroup = (
  group: string
): keyof ArticlePrice => {
  const groupMap: Record<string, keyof ArticlePrice> = {
    A: "CMSH",
    B: "CMSH1",
    C: "CMSH2",
    D: "CMSH3",
    E: "CMSH4",
    F: "CMSH5",
    G: "CMSH6",
    H: "CMSH7",
    I: "CMSH8",
    J: "CMSH9",
    K: "CMSH10",
    L: "CMSH11",
    M: "CMSH12",
    N: "CMSH13",
    O: "CMSH14",
    P: "CMSH15",
    Q: "CMSH16",
    R: "CMSH17",
    S: "CMSH18",
    T: "CMSH19",
  };

  return groupMap[group.toUpperCase()] || "CMSH";
};

/**
 * Get user price
 */
export const getUserPrice = (article: ArticlePrice): number => {
  const user = getCurrentUser();
  const group = getUserGroup(user);
  const field = getPriceFieldByGroup(group);
  const price = article[field];

  if (price && price > 0) {
    return price;
  }

  return article.CMSH || 0;
};

/**
 * Retail price is always CMSH4
 */
export const getUserRetailPrice = (article: ArticlePrice): number => {
  return article.CMSH4 && article.CMSH4 > 0
    ? article.CMSH4
    : article.CMSH || 0;
};

/**
 * Currency logic
 */
export const getCurrencyByGroup = (group: string): string =>
  group.toUpperCase() === "A" ? "CHF" : "CHF";

export const getCurrentUserCurrency = (): string =>
  getCurrencyByGroup(getUserGroup(getCurrentUser()));

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: "€",
  LEK: "LEK",
  CHF: "CHF",
  PLN: "PLN",
  USD: "$",
  GBP: "£",
};

export const formatPriceWithCurrency = (
  price: number | string | null | undefined,
  group?: string,
  currencyOverride?: string
): string => {
  const num = typeof price === "number" ? price : (typeof price === "string" ? parseFloat(price) : NaN);
  const value = !isNaN(num) ? num : 0;
  const currency = currencyOverride ?? getCurrencyByGroup(
    group || getUserGroup(getCurrentUser())
  );
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;

  return `${value.toFixed(2)} ${symbol}`;
};
